<script setup>
import { CsCard } from '../card';
import { CsColorPickerContent } from '../color-picker';
import { CsFormError, CsFormHelpText, CsFormLabel } from '../form';
import { CsIcon } from '../icon';
import { CsInput } from '../input';
import { CsPopover, CsPopoverContent, CsPopoverTrigger } from '../popover';
import { CsText } from '../text';

import { computed, ref } from 'vue';
import { getColorType } from './utils';

const props = defineProps({
  value: {
    type: String,
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
  error: {
    type: String,
    default: '',
  },
  helpText: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['input', 'change']);

const selectedCustomColor = ref('');

const isValidColor = computed(() => {
  if (!selectedCustomColor.value) {
    return true;
  }

  return selectedCustomColor.value.match(/^#([0-9a-f]{6})$/i);
});

const colorType = computed(() => {
  if (!props.value) return '';

  return getColorType(props.value);
});

function handleSelectColor(color) {
  emit('input', color);
  emit('change', color);
}
</script>

<template>
  <div data-component="LabelColorPicker" class="flex flex-col">
    <CsFormLabel v-if="props.label" class="mb-1">
      {{ props.label }}
    </CsFormLabel>

    <div class="flex flex-col gap-2">
      <CsPopover v-slot="{ close }" is-teleport>
        <CsPopoverTrigger>
          <div
            class="relative h-8 w-8 cursor-pointer rounded-full transition-opacity hover:opacity-80"
            :style="{
              backgroundColor: colorType === 'custom' ? props.value : undefined,
            }"
          />
        </CsPopoverTrigger>

        <CsPopoverContent>
          <CsCard class="flex flex-col shadow-brand-500">
            <header
              class="flex h-[44px] items-center justify-between rounded-t-lg border-b border-brand-border bg-brand-surface-secondary px-4 py-3"
            >
              <CsText weight="bold"> Color picker </CsText>

              <button
                class="absolute right-2 top-2 flex h-7 w-7 cursor-pointer items-center justify-center rounded-lg text-brand-gray-12 transition-colors hover:bg-brand-gray-8 active:bg-brand-gray-9 dark:hover:bg-brand-background-secondary"
                @click="close"
              >
                <CsIcon name="x" />
              </button>
            </header>

            <div class="flex flex-col gap-3 p-4">
              <CsColorPickerContent
                v-model="selectedCustomColor"
                @input="handleSelectColor"
              />

              <CsInput
                v-model="selectedCustomColor"
                :is-clearable="false"
                placeholder="#000000"
                :error="isValidColor ? '' : 'Invalid color format'"
              >
                <template #prefix>
                  <CsText
                    variant="body-sm"
                    variant-color="secondary"
                    class="mr-0.5"
                  >
                    Hex
                  </CsText>
                </template>
              </CsInput>
            </div>
          </CsCard>
        </CsPopoverContent>
      </CsPopover>
    </div>

    <CsFormHelpText v-if="props.helpText" class="mt-1">
      {{ props.helpText }}
    </CsFormHelpText>

    <CsFormError v-if="props.error" class="mt-1">
      {{ props.error }}
    </CsFormError>
  </div>
</template>

<style>
.custom-color-swatch {
  position: absolute;
  top: 0px;
  left: 0px;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  box-sizing: border-box;
  transform: rotateZ(210deg);
  background: conic-gradient(red, yellow, lime, aqua, blue, magenta, red);
}

.custom-color-bg {
  position: absolute;
  top: 0px;
  left: 0px;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  box-sizing: border-box;
  background: radial-gradient(
    circle closest-side,
    rgb(255, 255, 255),
    transparent
  );
}
</style>
